import React, { useEffect, useState } from 'react';

import "vanilla-cookieconsent/dist/cookieconsent.css";
import "../cookie-consent-overrides.css";
import * as CookieConsent from "vanilla-cookieconsent";

import cookieTranslationData from '../translations/cookieconsent.en.json'

import {
  APP_CONFIG,
  API_IDENTITY_SERVER_URL,
  IS_LOCALHOST,
  GATSBY_ENV,
  getDurationBetweenDates,
  deepCopy,
  CookieService,
} from '../utils';

const hasPropyCookieConsent = () => {
    return document.cookie.indexOf('propy_cookie=') !== -1;
}

// Clarity was added to the analytics category after many visitors had already consented, and the Angular
// app (which shares propy_cookie) doesn't define it, so their consents don't list it and every save made
// there drops it again. Clarity therefore follows the analytics category, unless the visitor switched it
// off in our preferences modal: that choice is kept in the consent's custom data.
const CLARITY_ACCEPTED = 'clarityAccepted';

const includeHubspotScriptLoaderAndDisableBanner = () => {
    if(APP_CONFIG.HUBSPOT_ID) {
        const disableHsBannerScript = document.createElement('script');
        disableHsBannerScript.setAttribute('type', 'text/javascript');
        disableHsBannerScript.innerHTML = `window.disableHubSpotCookieBanner = true;`;
        document.head.appendChild(disableHsBannerScript);

        const scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'text/javascript');
        scriptElement.setAttribute('id', 'hs-script-loader');
        scriptElement.setAttribute('async', '');
        scriptElement.setAttribute('defer', '');
        scriptElement.setAttribute('src', `//js.hs-scripts.com/${APP_CONFIG.HUBSPOT_ID}.js`);
        document.head.appendChild(scriptElement);

        // test only logging
        const anScript = document.createElement('script');
        anScript.setAttribute('type', 'text/javascript');
        anScript.innerHTML = `
        var _hsp = (window._hsp = window._hsp || []);
        _hsp.push([
            'addPrivacyConsentListener',
                function (consent) {
                    console.log("hs analytics:", consent.categories.analytics);
                    console.log("hs advertisement:", consent.categories.advertisement);
                    console.log("hs functionality:", consent.categories.functionality);
                },
            ]);
        `;
        document.head.appendChild(anScript);
    }
}

const setHubspotConsentStatus = (status) => {
    const includeHsConsentScript = document.createElement('script');
    includeHsConsentScript.setAttribute('type', 'text/javascript');
    includeHsConsentScript.innerHTML = `
        window._hsp = window._hsp || [];
        window._hsp.push([
        'setHubSpotConsent',
        {
            analytics: ${status.analytics},
            advertisement: ${status.advertisement},
            functionality: ${status.functionality},
        },
        ]);
    `;
    document.head.appendChild(includeHsConsentScript);
}

const CookieManager = (props) => {

  const reloadPage = () => {
    window.location.reload();
  }

  const [propyCookieBeforeChange, setPropyCookieBeforeChange] = useState(JSON.parse((CookieService.get('propy_cookie').length > 0) ? CookieService.get('propy_cookie') : '{}'));
  const [needsReload, setNeedsReload] = useState(false);

  useEffect(() => {
    if(needsReload) {
      reloadPage();
    }
  }, [needsReload])

  let {
    setPropyCookieValue,
  } = props;

  const getDomainFromPath = (path) => {
    let parts = path.split('https://');
    if (parts?.length === 2) {
        return parts[1];
    } else {
        parts = path.split('http://');
        if (parts?.length === 2) {
            return parts[1];
        }
        return path;
    }
  }

  const getPropyDomainForStripe = (envName) => {
      if (envName === 'development') {
          return '.dev.propy.com';
      } else if (envName === 'stage') {
          return '.stage.propy.com';
      } else {
          return '.propy.com';
      }
  }

  const deleteWalletConnectCookies = () => {
      CookieConsent.eraseCookies(['__cf_bm'], '/');
  }

  const deleteListhubCookies = () => {
      CookieConsent.eraseCookies(['AWSALB', 'AWSALBCORS', 'AWSALBTG', 'AWSALBTGCORS', 'lhac'], '/');
  }

  const deleteStripeCookies = () => {
      CookieConsent.eraseCookies(['__stripe_sid', '__stripe_mid', 'm'], '/');
  }

  const deletePlaidCookies = () => {
      CookieConsent.eraseCookies(['__Secure-sfpc', '_iidt', 'locale', 'referrer_url', 'entry_url'], '/');
  }

  const deleteTwitterAnalyticsCookies = () => {
    CookieConsent.eraseCookies(['guest_id', 'personalization_id'], '/');
  }

  const deleteIntercomCookies = () => {
      CookieConsent.eraseCookies(
          ['intercom-device-id-zpladqap', 'intercom-session-zpladqap', 'intercom-id-zpladqap'],
          '/'
      );
  }

  const deleteAnalyticsCookies = () => {
      CookieConsent.eraseCookies([/^(_ga)/, '_gid'], '/');
  }

  const deleteSmartlookCookies = () => {
      CookieConsent.eraseCookies(['SL_C_23361dd035530_SID'], '/');
  }

  const deleteClarityCookies = () => {
      CookieConsent.eraseCookies(['_clck', '_clsk'], '/');
  }

  const deleteGoogleTagManagerCookies = () => {
      CookieConsent.eraseCookies(['_gcl_au'], '/');
  }

  const deleteFacebookCookies = () => {
      CookieConsent.eraseCookies(['_fbp'], '/');
  }

  const deleteHubspotAnalyticsCookies = () => {
      CookieConsent.eraseCookies(['__hstc', '__hssc', '__hssrc', 'hubspotutk'], '/');
  }

  const deleteHubspotAdvertisingCookies = () => {
      CookieConsent.eraseCookies(['datr', 'sb'], '/');
  }

  const storePropyCookie = (cookie) => {
      if (cookie.categories.includes('analytics')) {
          cookie = { ...cookie, data: { ...cookie.data, [CLARITY_ACCEPTED]: cookie.services.analytics.includes('clarity') } };
      }
      const newCookie = JSON.stringify(cookie);
      const domain = IS_LOCALHOST ? '.localhost' : '.propy.com';
      const propyCookie = CookieService.get('propy_cookie');
      if (propyCookie?.length > 0) {
        const consentTime = new Date(JSON.parse(propyCookie).consentTimestamp).getTime();
        const expTime = 365 * 24 * 60 * 60 * 1000;
        const expDate = new Date(consentTime + expTime);
        if (getDurationBetweenDates(new Date(), expDate) <= 0) {
            // expired cookie consent logic
            setPropyCookieValue('{}');
            CookieService.delete('propy_cookie');
            setTimeout(() => CookieConsent.show(true), 0);
        } else {
            setPropyCookieValue(newCookie);
            CookieService.set('propy_cookie', newCookie, expDate, '/', domain, true, 'None');
        }
      } else {
        setPropyCookieValue(newCookie);
        CookieService.set('propy_cookie', newCookie, 365, '/', domain, true, 'None');
      }
  }

  const addClarityToAnalyticsConsent = () => {
      const propyCookie = CookieService.get('propy_cookie');
      if (!(propyCookie?.length > 0)) {
          return;
      }
      const cookie = JSON.parse(propyCookie);
      const analyticsServices = cookie.services?.analytics || [];
      if (
          !cookie.consentId ||
          !cookie.categories?.includes('analytics') ||
          analyticsServices.includes('clarity') ||
          cookie.data?.[CLARITY_ACCEPTED] === false
      ) {
          return;
      }
      storePropyCookie({
          ...cookie,
          services: {
              ...cookie.services,
              analytics: [...analyticsServices, 'clarity'],
          },
      });
  }

  const manageHubspotConsent = (cookie) => {
      const status = {
          analytics: false,
          advertisement: false,
          functionality: false,
      };
      if (
          cookie.categories.includes('necessary') &&
          cookie?.services?.necessary &&
          cookie?.services?.necessary.includes('hubspot')
      ) {
          status.analytics = true;
      }
      if (
          cookie.categories.includes('advertising') &&
          cookie?.services?.advertising &&
          cookie?.services?.advertising.includes('hs')
      ) {
          status.advertisement = true;
      }
      setHubspotConsentStatus(status);
      if (!status.analytics) {
          deleteHubspotAnalyticsCookies();
      }
      if (!status.advertisement) {
          deleteHubspotAdvertisingCookies();
      }
  }

  const manageTwitterConsent = (cookie) => {
    const status = {
        analytics: false,
    };
    if (
        cookie.categories.includes('analytics') &&
        cookie?.services?.analytics &&
        cookie?.services?.analytics.includes('twitter')
    ) {
        status.analytics = true;
    }
    if (!status.analytics) {
        deleteTwitterAnalyticsCookies();
    }
  }

  const onConsent = (cookie) => {
      includeHubspotScriptLoaderAndDisableBanner();
    //   if (!baseEnvironment.china) {
    //       includeGoogleMaps();
    //   }
    //   includeGoogleStreetView();
    //   includeInfoBubble();
      storeConsentCookie(cookie);
  }

  const storeConsentCookie = (cookie) => {
      const consentTime = new Date(cookie.consentTimestamp).getTime();
      const expTime = 365 * 24 * 60 * 60 * 1000;
      const expDate = new Date(consentTime + expTime);
      const domain = IS_LOCALHOST ? '.localhost' : '.propy.com';
      CookieService.set('cookieconsent_status', 'dismiss', expDate, '/', domain, false, 'None');
  }

  const deleteConsentCookie = () => {
      const domain = IS_LOCALHOST ? '.localhost' : '.propy.com';
      CookieService.delete('cookieconsent_status', '/', domain, false, 'None');
  }

  const onMissingPropyCookie = () => {
    //   if (!baseEnvironment.china) {
    //       excludeGoogleMaps();
    //   }
    //   excludeGoogleStreetView();
    //   excludeInfoBubble();
      deleteConsentCookie();

      // use with autoShow: false
      setTimeout(() => CookieConsent.show(true), 1000);
  }

  const restoreCookiesConsentFromUserPreferences = () => {
      // workaround because of Cookieconsent issue  with cookie values while multiple opening the consent modal before page reload
      const propyCookie = CookieService.get('propy_cookie');
      if (propyCookie?.length > 0) {
          const cookieValue = JSON.parse(propyCookie);
          if (cookieValue.services) {
              // necessary
              if (cookieValue.services?.necessary?.length > 0) {
                  CookieConsent.acceptService(cookieValue.services?.necessary, 'necessary');
              }

              // functional
              if (cookieValue.services?.functional?.length > 0) {
                  CookieConsent.acceptService(cookieValue.services?.functional, 'functional');
              }

              // analytics
              if (cookieValue.services?.analytics?.length > 0) {
                  CookieConsent.acceptService(cookieValue.services?.analytics, 'analytics');
              }

              // advertising
              if (cookieValue.services?.advertising?.length > 0) {
                  CookieConsent.acceptService(cookieValue.services?.advertising, 'advertising');
              }
          }
      }
  }

    const checkCookieChanges = () => {
        const cookie = CookieConsent.getCookie();
        if (propyCookieBeforeChange) {
            let triggerReload = false;
            // walletConnect
            if (
                !cookie.services?.functional.includes('walletConnect') &&
                propyCookieBeforeChange?.services?.functional.includes('walletConnect')
            ) {
                triggerReload = true;
            }
            // listhub
            if (
                !cookie.services?.functional.includes('listhub') &&
                propyCookieBeforeChange?.services?.functional.includes('listhub')
            ) {
                triggerReload = true;
            }
            // stripe
            if (
                !cookie.services?.functional.includes('stripe') &&
                propyCookieBeforeChange?.services?.functional.includes('stripe')
            ) {
                triggerReload = true;
            }
            // plaid
            if (
                !cookie.services?.functional.includes('plaid') &&
                propyCookieBeforeChange?.services?.functional.includes('plaid')
            ) {
                triggerReload = true;
            }
            // twitter
            if (
                !cookie.services?.analytics.includes('twitter') &&
                propyCookieBeforeChange?.services?.analytics.includes('twitter')
            ) {
                triggerReload = true;
            }
            if(triggerReload) {
                setNeedsReload(true);
            }
        }
    }

    useEffect(() => {
        // before CookieConsent.run() reads the cookie, so the preferences modal shows Clarity as accepted
        addClarityToAnalyticsConsent();
        CookieConsent.run(
            {
            mode: 'opt-in',
            autoShow: true,
            revision: 0,
            manageScriptTags: true,
            autoClearCookies: false,
            hideFromBots: true,
            disablePageInteraction: false,
            lazyHtmlGeneration: true,
            cookie: {
                name: 'propy_cookie',
                domain: '.propy.com',
                path: '/',
                expiresAfterDays: 365,
                sameSite: 'None',
                useLocalStorage: false,
            },
            categories: {
                necessary: {
                    enabled: true,
                    readOnly: true,
                    services: {
                        identityServer: {
                            label: 'IdentityServer',
                            onAccept: () => {},
                            onReject: () => {},
                            cookies: [
                                {
                                    name: 'cookieconsent_status',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: '.AspNetCore.Identity.Application',
                                    domain: getDomainFromPath(API_IDENTITY_SERVER_URL),
                                    path: '/',
                                },
                                {
                                    name: 'idsrv.session',
                                    domain: getDomainFromPath(API_IDENTITY_SERVER_URL),
                                    path: '/',
                                },
                                {
                                    name: '.AspNetCore.Antiforgery.zbjUl449ekk',
                                    domain: getDomainFromPath(API_IDENTITY_SERVER_URL),
                                    path: '/',
                                },
                            ],
                        },
                        captcha: {
                            label: 'Captcha',
                            onAccept: () => {},
                            onReject: () => {},
                            cookies: [
                                {
                                    name: '_GRECAPTCHA',
                                    domain: 'www.google.com',
                                    path: '/recaptcha',
                                },
                            ],
                        },
                        hubspot: {
                            label: 'Hubspot (Necessary)',
                            onAccept: () => {},
                            onReject: () => {},
                            cookies: [
                                {
                                    name: '__hs_opt_out',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__hs_do_not_track',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__hs_initial_opt_in',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__hs_cookie_cat_pref',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__hs_gpc_banner_dismiss',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__hs_notify_banner_dismiss',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: 'hs_ab_test',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: /_key$/,
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: 'hs-messages-is-open',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: 'hs-messages-hide-welcome-message',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__hsmem',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: 'hs-membership-csrf',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: 'hublytics_events_53',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: /^hs\.superstore\.laboratory\./,
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__cfruid',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__cfuvid',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__cf_bm',
                                    domain: '.hubspot.com',
                                    path: '/',
                                },
                                {
                                    name: '__hstc',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: '__hssc',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: '__hssrc',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: 'hubspotutk',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                        linkedin: {
                            label: 'Linkedin',
                            onAccept: () => {},
                            onReject: () => {},
                            cookies: [
                                {
                                    name: 'bcookie',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'li_gc',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'lidc',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'bscookie',
                                    domain: 'www.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'lang',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'li_mc',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'Ims_ads',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'Ims_analytics',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                                {
                                    name: 'li_sugr',
                                    domain: '.linkedin.com',
                                    path: '/',
                                },
                            ],
                        },
                    },
                },
                functional: {
                    enabled: true,
                    readOnly: false,
                    services: {
                        wagmi: {
                            label: 'Wagmi',
                            onAccept: () => {},
                            onReject: () => {},
                            cookies: [],
                        },
                        walletConnect: {
                            label: 'WalletConnect',
                            onAccept: () => {
                                // this.externalWalletService.initWalletConnection();
                            },
                            onReject: () => {
                                // this.externalWalletService.disableWalletConnection();
                                deleteWalletConnectCookies();
                            },
                            cookies: [
                                {
                                    name: '__cf_bm',
                                    domain: '.walletconnect.com',
                                },
                            ],
                        },
                        listhub: {
                            label: 'Listhub',
                            onAccept: () => {
                                // includeListhub();
                            },
                            onReject: () => {
                                // excludeListhub();
                                deleteListhubCookies();
                            },
                            cookies: [
                                {
                                    name: 'AWSALB',
                                    domain: 'tracking.listhub.net',
                                    path: '/',
                                },
                                {
                                    name: 'AWSALBCORS',
                                    domain: 'tracking.listhub.net',
                                    path: '/',
                                },
                                {
                                    name: 'AWSALBTG',
                                    domain: 'tracking.listhub.net',
                                    path: '/',
                                },
                                {
                                    name: 'AWSALBTGCORS',
                                    domain: 'tracking.listhub.net',
                                    path: '/',
                                },
                                {
                                    name: 'lhac',
                                    domain: '.listhub.net',
                                    path: '/',
                                },
                            ],
                        },
                        stripe: {
                            label: 'Stripe',
                            onAccept: () => {
                                // includeStripe();
                            },
                            onReject: () => {
                                // excludeStripe();
                                deleteStripeCookies();
                            },
                            cookies: [
                                {
                                    name: '__stripe_sid',
                                    domain: getPropyDomainForStripe(GATSBY_ENV),
                                    path: '/',
                                },
                                {
                                    name: '__stripe_mid',
                                    domain: getPropyDomainForStripe(GATSBY_ENV),
                                    path: '/',
                                },
                                {
                                    name: '__stripe_mid',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: '__stripe_mid',
                                    domain: 'fontawesome.com',
                                    path: '/',
                                },
                                {
                                    name: 'm',
                                    domain: 'm.stripe.com',
                                    path: '',
                                },
                            ],
                        },
                        plaid: {
                            label: 'Plaid',
                            onAccept: () => {
                                // includePlaid();
                            },
                            onReject: () => {
                                // excludePlaid();
                                deletePlaidCookies();
                            },
                            cookies: [
                                {
                                    name: '__Secure-sfpc',
                                    domain: '.plaid.com',
                                    path: '/',
                                },
                                {
                                    name: '_iidt',
                                    domain: '.plaid.com',
                                    path: '/',
                                },
                                {
                                    name: 'locale',
                                    domain: '.plaid.com',
                                    path: '/',
                                },
                                {
                                    name: 'referrer_url',
                                    domain: '.plaid.com',
                                    path: '/',
                                },
                                {
                                    name: 'entry_url',
                                    domain: '.plaid.com',
                                    path: '/',
                                },
                            ],
                        },
                        intercom: {
                            label: 'Intercom',
                            onAccept: () => {
                                // this.contactSupportService.includeIntercomForPersonalUser(this.userInfo);
                            },
                            onReject: () => {
                                // this.contactSupportService.excludeIntercomForPersonalUser();
                                deleteIntercomCookies();
                            },
                            cookies: [
                                {
                                    name: 'intercom-device-id-zpladqap',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: 'intercom-session-zpladqap',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: 'intercom-id-zpladqap',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                    },
                },
                analytics: {
                    enabled: false,
                    readOnly: false,
                    services: {
                        ga: {
                            label: 'Google Analytics',
                            onAccept: () => {
                                // if (!baseEnvironment.china) {
                                //     this.googleAnalyticsService.setupGoogleAnalytics();
                                //     this.googleAnalyticsService.setUserId(this.userInfo);
                                // }
                            },
                            onReject: () => {
                                // if (!baseEnvironment.china) {
                                //     this.googleAnalyticsService.removeGoogleAnalytics();
                                // }
                                deleteAnalyticsCookies();
                            },
                            cookies: [
                                {
                                    name: '_ga',
                                    domain: '.stripe.com',
                                    path: '/',
                                },
                                {
                                    name: '_ga_5YXR82RXMV',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: /^(_ga)/, // regex
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: '_gid',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                        smartlook: {
                            label: 'Smartlook',
                            onAccept: () => {
                                // this.smartLookService.init();
                            },
                            onReject: () => {
                                // this.smartLookService.disable();
                                deleteSmartlookCookies();
                            },
                            cookies: [
                                {
                                    name: 'SL_C_23361dd035530_SID',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                        gtm: {
                            label: 'Google Tag Manager',
                            onAccept: () => {
                                // includeGoogleTagManager(baseEnvironment);
                            },
                            onReject: () => {
                                // excludeGoogleTagManager(baseEnvironment);
                                deleteGoogleTagManagerCookies();
                            },
                            cookies: [
                                {
                                    name: '_gcl_au',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                        fb: {
                            label: 'Facebook',
                            onAccept: () => {
                                //   includeFbSdk();
                                //   includeFacebookEvents();
                            },
                            onReject: () => {
                                //   excludeFbSdk();
                                //   excludeFacebookEvents();
                                deleteFacebookCookies();
                            },
                            cookies: [
                                {
                                    name: '_fbp',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                        lfa: {
                            label: 'Leadfeeder',
                            onAccept: () => {},
                            onReject: () => {},
                            cookies: [
                                {
                                    name: '_lfa',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                        twitter: {
                            label: 'Twitter',
                            onAccept: () => {},
                            onReject: () => {
                                deleteTwitterAnalyticsCookies();
                            },
                            cookies: [
                                {
                                    name: 'guest_id',
                                    domain: '.twitter.com',
                                    path: '/',
                                },
                                {
                                    name: 'personalization_id',
                                    domain: '.twitter.com',
                                    path: '/',
                                },
                                {
                                    name: 'guest_id_ads',
                                    domain: '.twitter.com',
                                    path: '/',
                                },
                                {
                                    name: 'guest_id_marketing',
                                    domain: '.twitter.com',
                                    path: '/',
                                },
                            ],
                        },
                        clarity: {
                            label: 'Microsoft Clarity',
                            onAccept: () => {},
                            onReject: () => {
                                deleteClarityCookies();
                            },
                            cookies: [
                                {
                                    name: '_clck',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                                {
                                    name: '_clsk',
                                    domain: '.propy.com',
                                    path: '/',
                                },
                            ],
                        },
                    },
                },
                advertising: {
                    enabled: false,
                    readOnly: false,
                    services: {
                        hs: {
                            label: 'Hubspot (Ads)',
                            onAccept: () => {},
                            onReject: () => {},
                            cookies: [
                                {
                                    name: 'datr',
                                    domain: '.facebook.com',
                                    path: '/',
                                },
                                {
                                    name: 'sb',
                                    domain: '.facebook.com',
                                    path: '/',
                                },
                            ],
                        },
                    },
                },
            },
            language: {
                default: 'en',
                translations: {
                    en: async () => {
                        return cookieTranslationData
                    },
                },
            },
            guiOptions: {
                consentModal: {
                    layout: 'bar inline',
                    position: 'bottom',
                    flipButtons: false,
                    equalWeightButtons: false,
                },
                preferencesModal: {
                    layout: 'box',
                    flipButtons: true,
                    equalWeightButtons: true,
                },
            },
            onFirstConsent: ({ cookie }) => {
                // Callback function executed once, on the user's first consent action.
                setNeedsReload(true);
            },
            onConsent: ({ cookie }) => {
                // Callback function executed on the user's first consent action and after each page load.
                storePropyCookie(cookie);
                onConsent(cookie);
                manageHubspotConsent(cookie);
                manageTwitterConsent(cookie);
            },
            onChange: ({ cookie, changedCategories, changedServices }) => {
                // Callback function executed when the user's preferences — such as accepted categories and services — change.
                setPropyCookieBeforeChange(JSON.parse(CookieService.get('propy_cookie').length > 0 ? CookieService.get('propy_cookie') : '{}'))
                const storeCookie = deepCopy(cookie);
                storePropyCookie(storeCookie);
                manageHubspotConsent(storeCookie);
                manageTwitterConsent(storeCookie);
                // if (this.closeConsentModal) {
                //     this.closeConsentModal = false;
                    checkCookieChanges();
                // }
            },
            onModalShow: ({ modalName }) => {
                // consentModal or preferencesModal
            },
            onModalHide: ({ modalName }) => {
                if (modalName === 'preferencesModal') {
                    // this.closePreferencesModal = true;
                    checkCookieChanges();
                    // if (this.needPageReload) {
                    //     this.reloadPage();
                    // }
                } else if (modalName === 'consentModal') {
                    // if (this.closePreferencesModal) {
                    //     this.closePreferencesModal = false;
                    // } else {
                    //     this.closeConsentModal = true;
                    // }
                }
            },
            onModalReady: ({ modalName, modal }) => {
                if (modalName === 'preferencesModal' && hasPropyCookieConsent()) {
                    restoreCookiesConsentFromUserPreferences();
                } else {
                    setPropyCookieValue('{}');
                }
            },
        }
        );
    });


    // TODO might need to use the cookie clearing functionality from here
    // useEffect(() => {
    //     if(!consentToCookies) {
    //         try {
    //             //@ts-ignore
    //             if(!window.document.__defineGetter__) {
    //             Object.defineProperty(document, 'cookie', {
    //                 get: function(){return ''},
    //                 set: function(){return true},
    //             });
    //             } else {
    //             //@ts-ignore
    //             window.document.__defineGetter__("cookie", function() { return '';} );
    //             //@ts-ignore
    //             window.document.__defineSetter__("cookie", function() {} );
    //             }
    //         } catch (e) {
    //             console.error("Error encountered when attempting to block cookie functionality", e);
    //         }
    //     } else if (APP_CONFIG.SMARTLOOK_ID) {

    //         const script = document.createElement('script')
    //         script.innerHTML = `
    //             window.smartlook||(function(d) {
    //         var o=smartlook=function(){ o.api.push(arguments)},h=d.getElementsByTagName('head')[0];
    //         var c=d.createElement('script');o.api=new Array();c.async=true;c.type='text/javascript';
    //         c.charset='utf-8';c.src='https://web-sdk.smartlook.com/recorder.js';h.appendChild(c);
    //         })(document);
    //         smartlook('init', '${APP_CONFIG.SMARTLOOK_ID}', { region: 'eu' });
    //         `
    //         document.head.appendChild(script)
    //         // const script2 = document.createElement('script');
    //         // script.type = 'text/javascript';
    //         // script.id = 'hs-script-loader';
    //         // script.async = true;
    //         // script.defer = true;
    //         // script.src = '//js.hs-scripts.com/4204561.js';

    //         // document.head.appendChild(script);

    //         // Cleanup function to remove the script when the component unmounts or when consentToCookies changes to false
    //         return () => {
    //             document.head.removeChild(script);
    //         };
    //     }
    // }, [consentToCookies])

    return (
        <></>
    );
}

export default CookieManager;